import { useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import { Button, Tooltip } from "@mui/material";
import { gql } from "generated-graphql";
import { useLongPollingForJobWithFileOutput } from "hooks/useLongPollingForJobWithFileOutput";
import { useCallback } from "react";
import { useAlerts } from "./Alerts/AlertProvider";

interface DownloadAllButtonProps {
  reportId: string;
  tooltipTitle?: string;
}

export const DownloadAllButton: React.FC<DownloadAllButtonProps> = ({
  reportId,
  tooltipTitle = "Download all documents generated during submission",
}) => {
  const alerts = useAlerts();
  const [generateDeliverables, { loading: isGeneratingDeliverables }] =
    useMutation(
      gql(`
      mutation DownloadTierIIDeliverablesByReport($reportId: ID!) {
        downloadTierIIDeliverablesByReport(reportId: $reportId) {
          jobId
        }
      }
    `)
    );

  const handleUrlsReady = useCallback((urls: string[]) => {
    alerts.success("Deliverables ready, downloading...");
    for (const url of urls) {
      window.open(url, "_blank");
    }
  }, []);

  const handleError = useCallback(
    (err: Error) => {
      alerts.error(err.message);
    },
    [alerts]
  );

  const { polling, startPolling } = useLongPollingForJobWithFileOutput({
    timeoutInSeconds: 1200,
    onUrlsReady: handleUrlsReady,
    onError: handleError,
  });

  const onClickDownloadAll = useCallback(async () => {
    await generateDeliverables({
      variables: {
        reportId,
      },
      onCompleted: (data) => {
        const jobId = data.downloadTierIIDeliverablesByReport.jobId;
        startPolling(jobId);
        alerts.info("Packaging deliverables, this may take a minute.");
      },
      onError: (error) => {
        alerts.error(error.message);
      },
    });
  }, [reportId, generateDeliverables, startPolling, alerts]);

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        size="small"
        startIcon={<Download />}
        loading={isGeneratingDeliverables || polling}
        loadingPosition="start"
        onClick={onClickDownloadAll}
      >
        Deliverables
      </Button>
    </Tooltip>
  );
};
